/**
 * React Router 6 data router.
 *
 * Route tree:
 *   /login                          public
 *   /                               auth-gated landing redirect
 *
 *   /admin/*                        admin-only, wrapped in AdminLayout (top bar, sidebar)
 *     /admin/agents                 Phase 4 — real
 *     /admin/agents/new             Phase 4 — editor
 *     /admin/agents/:id             Phase 4 — editor
 *     /admin/users                  Phase 1 — real
 *     /admin/users/:id              Phase 4 — detail w/ assignment
 *     /admin/sheets                 Phase 5 — real
 *     /admin/sheets/new             Phase 5 — editor
 *     /admin/sheets/:id             Phase 5 — detail w/ access panel
 *     /admin/sheets/:id/edit        Phase 5 — editor
 *     /admin/conversations          read-only platform-wide audit list
 *     /admin/conversations/:id      read-only message-thread detail
 *     /admin/settings               Phase 2 — real
 *
 *   /chat                           user portal (UserLayout)
 *     /chat/:agentId                stub until Phase 7
 *   /sheets/:sheetId                Phase 6 — real iframe view
 *
 *   *                               404
 */
import { lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { RequireAuth } from '../components/auth/RequireAuth';
// Public shell + route guards stay eager: they're small and gate the first
// paint. Everything behind the two authenticated layouts is lazy-loaded below
// (audit PERF-2) — each becomes its own chunk instead of riding the initial
// bundle. The layouts render <Outlet/> inside a <Suspense>, so every lazy page
// mounts under a boundary by construction.
// CommunicationGuard is eager (it wraps the route), and it lives in the same
// module as CommunicationPage — so the page rides along eagerly too rather
// than getting an ineffective lazy wrapper.
import { CommunicationGuard, CommunicationPage } from '../pages/communication/CommunicationPage';
import { HRGuard } from '../pages/hr/HRGuard';
import { LoginPage } from '../pages/LoginPage';
import { AboutPage } from '../pages/public/AboutPage';
import { ContactPage } from '../pages/public/ContactPage';
import { LandingPage } from '../pages/public/LandingPage';
import { SupportGuard } from '../pages/support/SupportGuard';

import { AdminLayout } from './layouts/AdminLayout';
import { UserLayout } from './layouts/UserLayout';

const SecurityPage = lazy(() =>
  import('../pages/account/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const AgentEditorPage = lazy(() =>
  import('../pages/admin/AgentEditorPage').then((m) => ({ default: m.AgentEditorPage })),
);
const AgentsListPage = lazy(() =>
  import('../pages/admin/AgentsListPage').then((m) => ({ default: m.AgentsListPage })),
);
const AuditLogPage = lazy(() =>
  import('../pages/admin/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);
const AutomationControlPage = lazy(() =>
  import('../pages/admin/automation/AutomationControlPage').then((m) => ({
    default: m.AutomationControlPage,
  })),
);
const ConversationsAuditDetailPage = lazy(() =>
  import('../pages/admin/ConversationsAuditDetailPage').then((m) => ({
    default: m.ConversationsAuditDetailPage,
  })),
);
const ConversationsAuditPage = lazy(() =>
  import('../pages/admin/ConversationsAuditPage').then((m) => ({
    default: m.ConversationsAuditPage,
  })),
);
const DesignShowcasePage = lazy(() =>
  import('../pages/admin/DesignShowcasePage').then((m) => ({ default: m.DesignShowcasePage })),
);
const FolderDetailPage = lazy(() =>
  import('../pages/admin/FolderDetailPage').then((m) => ({ default: m.FolderDetailPage })),
);
const FolderEditorPage = lazy(() =>
  import('../pages/admin/FolderEditorPage').then((m) => ({ default: m.FolderEditorPage })),
);
const FoldersListPage = lazy(() =>
  import('../pages/admin/FoldersListPage').then((m) => ({ default: m.FoldersListPage })),
);
const LandingEditorPage = lazy(() =>
  import('../pages/admin/LandingEditorPage').then((m) => ({ default: m.LandingEditorPage })),
);
const OdooControlPage = lazy(() =>
  import('../pages/admin/odoo/OdooControlPage').then((m) => ({ default: m.OdooControlPage })),
);
const SettingsPage = lazy(() =>
  import('../pages/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SheetDetailPage = lazy(() =>
  import('../pages/admin/SheetDetailPage').then((m) => ({ default: m.SheetDetailPage })),
);
const SheetEditorPage = lazy(() =>
  import('../pages/admin/SheetEditorPage').then((m) => ({ default: m.SheetEditorPage })),
);
const SheetsListPage = lazy(() =>
  import('../pages/admin/SheetsListPage').then((m) => ({ default: m.SheetsListPage })),
);
const UserDetailPage = lazy(() =>
  import('../pages/admin/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
);
const UsersListPage = lazy(() =>
  import('../pages/admin/UsersListPage').then((m) => ({ default: m.UsersListPage })),
);
const UserAgentsListPage = lazy(() =>
  import('../pages/agents/UserAgentsListPage').then((m) => ({ default: m.UserAgentsListPage })),
);
const AutomationChatPage = lazy(() =>
  import('../pages/automation/AutomationChatPage').then((m) => ({ default: m.AutomationChatPage })),
);
const ChatLandingPage = lazy(() =>
  import('../pages/chat/ChatLandingPage').then((m) => ({ default: m.ChatLandingPage })),
);
const ChatThreadPage = lazy(() =>
  import('../pages/chat/ChatThreadPage').then((m) => ({ default: m.ChatThreadPage })),
);
const ContentCalendarPage = lazy(() =>
  import('../pages/content-calendar/ContentCalendarPage').then((m) => ({
    default: m.ContentCalendarPage,
  })),
);
const CrmPage = lazy(() => import('../pages/crm/CrmPage').then((m) => ({ default: m.CrmPage })));
const FilesViewPage = lazy(() =>
  import('../pages/files/FilesViewPage').then((m) => ({ default: m.FilesViewPage })),
);
const HRPage = lazy(() => import('../pages/hr/HRPage').then((m) => ({ default: m.HRPage })));
const LeadsPage = lazy(() =>
  import('../pages/leads/LeadsPage').then((m) => ({ default: m.LeadsPage })),
);
const MailPage = lazy(() =>
  import('../pages/mail/MailPage').then((m) => ({ default: m.MailPage })),
);
const MediaCalendarPage = lazy(() =>
  import('../pages/media-calendar/MediaCalendarPage').then((m) => ({ default: m.MediaCalendarPage })),
);
const OdooChatPage = lazy(() =>
  import('../pages/odoo/OdooChatPage').then((m) => ({ default: m.OdooChatPage })),
);
const ScraperPage = lazy(() =>
  import('../pages/scraper/ScraperPage').then((m) => ({ default: m.ScraperPage })),
);
const SeoDashboardPage = lazy(() => import('../pages/seo/SeoDashboardPage'));
const SeoSuggestionsPage = lazy(() => import('../pages/seo/SeoSuggestionsPage'));
const SheetViewPage = lazy(() =>
  import('../pages/sheets/SheetViewPage').then((m) => ({ default: m.SheetViewPage })),
);
const SupportPage = lazy(() =>
  import('../pages/support/SupportPage').then((m) => ({ default: m.SupportPage })),
);
const TelephonySupportPage = lazy(() =>
  import('../pages/support/TelephonySupportPage').then((m) => ({
    default: m.TelephonySupportPage,
  })),
);
const SupportTicketsPage = lazy(() =>
  import('../pages/support/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage })),
);
const SupportArchivePage = lazy(() =>
  import('../pages/support/SupportArchivePage').then((m) => ({ default: m.SupportArchivePage })),
);
const SupportContactsPage = lazy(() =>
  import('../pages/support/SupportContactsPage').then((m) => ({
    default: m.SupportContactsPage,
  })),
);
// UST-3159 — its own access grant, so no SupportGuard; the page checks it.
// Support Monitor has its own grant. The page shows a no-access state.
const SupportMonitorPage = lazy(() =>
  import('../pages/support/SupportMonitorPage').then((m) => ({
    default: m.SupportMonitorPage,
  })),
);
const SupportContractsPage = lazy(() =>
  import('../pages/support/SupportContractsPage').then((m) => ({
    default: m.SupportContractsPage,
  })),
);
const TemplateFillPage = lazy(() =>
  import('../pages/template-fill/TemplateFillPage').then((m) => ({ default: m.TemplateFillPage })),
);
const VideoEditorPage = lazy(() =>
  import('../pages/video-editor/VideoEditorPage').then((m) => ({ default: m.VideoEditorPage })),
);
const VisualsGeneratorPage = lazy(() =>
  import('../pages/visuals-generator/VisualsGeneratorPage').then((m) => ({
    default: m.VisualsGeneratorPage,
  })),
);

function NotFoundPage(): JSX.Element {
  return (
    <main className="grid min-h-full place-items-center px-6">
      <div className="text-center">
        <p className="font-mono text-xs uppercase tracking-tight text-ink-muted">404</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-ink">Not found</h1>
        <p className="mt-2 text-sm text-ink-muted">
          That page doesn&apos;t exist (yet — many routes land in later phases).
        </p>
      </div>
    </main>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/', element: <LandingPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/contact', element: <ContactPage /> },

  // ---------- Admin surface ----------
  {
    element: (
      <RequireAuth roles={['admin']}>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/admin', element: <Navigate to="/admin/users" replace /> },
      { path: '/admin/agents', element: <AgentsListPage /> },
      { path: '/admin/agents/new', element: <AgentEditorPage /> },
      { path: '/admin/agents/:id', element: <AgentEditorPage /> },
      { path: '/admin/users', element: <UsersListPage /> },
      { path: '/admin/users/:id', element: <UserDetailPage /> },
      { path: '/admin/sheets', element: <SheetsListPage /> },
      { path: '/admin/sheets/new', element: <SheetEditorPage /> },
      { path: '/admin/sheets/:id', element: <SheetDetailPage /> },
      { path: '/admin/sheets/:id/edit', element: <SheetEditorPage /> },
      { path: '/admin/files', element: <FoldersListPage /> },
      { path: '/admin/files/new', element: <FolderEditorPage /> },
      { path: '/admin/files/:id', element: <FolderDetailPage /> },
      { path: '/admin/files/:id/edit', element: <FolderEditorPage /> },
      { path: '/admin/media-calendar', element: <MediaCalendarPage /> },
      { path: '/admin/content-calendar', element: <ContentCalendarPage /> },
      { path: '/admin/visuals-generator', element: <VisualsGeneratorPage /> },
      { path: '/admin/seo', element: <SeoDashboardPage /> },
      { path: '/admin/seo/suggestions', element: <SeoSuggestionsPage /> },
      { path: '/admin/scraper', element: <ScraperPage /> },
      { path: '/admin/scraper/output', element: <ScraperPage /> },
      { path: '/admin/scraper/:agentId', element: <ScraperPage /> },
      { path: '/admin/scraper/:agentId/:sessionId', element: <ScraperPage /> },
      { path: '/admin/template-fill', element: <TemplateFillPage /> },
      { path: '/admin/video-editor', element: <VideoEditorPage /> },
      { path: '/admin/mail', element: <MailPage /> },
      { path: '/admin/leads', element: <LeadsPage /> },
      { path: '/admin/support', element: <SupportPage /> },
      { path: '/admin/support-tickets', element: <SupportTicketsPage /> },
      { path: '/admin/support-monitor', element: <SupportMonitorPage /> },
      { path: '/admin/support-archive', element: <SupportArchivePage /> },
      { path: '/admin/support-contacts', element: <SupportContactsPage /> },
      { path: '/admin/support-contracts', element: <SupportContractsPage /> },
      { path: '/admin/odoo-helpdesk', element: <SupportTicketsPage key="odoo-helpdesk" odoo /> },
      { path: '/admin/telephony-support', element: <TelephonySupportPage /> },
      { path: '/admin/communication', element: <CommunicationPage /> },
      { path: '/admin/hr', element: <HRPage /> },
      { path: '/admin/crm', element: <CrmPage /> },
      { path: '/admin/conversations', element: <ConversationsAuditPage /> },
      { path: '/admin/conversations/:id', element: <ConversationsAuditDetailPage /> },
      { path: '/admin/landing', element: <LandingEditorPage /> },
      { path: '/admin/audit', element: <AuditLogPage /> },
      { path: '/admin/odoo', element: <OdooControlPage /> },
      { path: '/admin/automation', element: <AutomationControlPage /> },
      { path: '/admin/settings', element: <SettingsPage /> },
      /* v2.1 design-system showcase — reachable via `/admin/_design`. Not
         linked from the sidebar; reviewers use it to verify every primitive
         renders correctly in light + dark before sweep PRs land. */
      { path: '/admin/_design', element: <DesignShowcasePage /> },
    ],
  },

  // ---------- User surface ----------
  {
    element: (
      <RequireAuth>
        <UserLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/chat', element: <ChatLandingPage /> },
      { path: '/chat/:agentId', element: <ChatThreadPage /> },
      { path: '/chat/:agentId/:conversationId', element: <ChatThreadPage /> },
      { path: '/odoo', element: <OdooChatPage /> },
      { path: '/odoo/:sessionId', element: <OdooChatPage /> },
      { path: '/automation', element: <AutomationChatPage /> },
      { path: '/automation/:automationKey', element: <AutomationChatPage /> },
      { path: '/automation/:automationKey/:sessionId', element: <AutomationChatPage /> },
      { path: '/sheets/:sheetId', element: <SheetViewPage /> },
      { path: '/files/:folderId', element: <FilesViewPage /> },
      { path: '/media-calendar', element: <MediaCalendarPage /> },
      { path: '/content-calendar', element: <ContentCalendarPage /> },
      { path: '/visuals-generator', element: <VisualsGeneratorPage /> },
      { path: '/seo', element: <SeoDashboardPage /> },
      { path: '/seo/suggestions', element: <SeoSuggestionsPage /> },
      { path: '/scraper', element: <ScraperPage /> },
      { path: '/scraper/output', element: <ScraperPage /> },
      { path: '/scraper/:agentId', element: <ScraperPage /> },
      { path: '/scraper/:agentId/:sessionId', element: <ScraperPage /> },
      { path: '/template-fill', element: <TemplateFillPage /> },
      { path: '/video-editor', element: <VideoEditorPage /> },
      { path: '/mail', element: <MailPage /> },
      { path: '/leads', element: <LeadsPage /> },
      {
        path: '/support',
        element: (
          <SupportGuard>
            <SupportPage />
          </SupportGuard>
        ),
      },
      {
        path: '/support-tickets',
        element: (
          <SupportGuard>
            <SupportTicketsPage />
          </SupportGuard>
        ),
      },
      {
        path: '/support-archive',
        element: (
          <SupportGuard>
            <SupportArchivePage />
          </SupportGuard>
        ),
      },
      {
        path: '/support-contacts',
        element: (
          <SupportGuard>
            <SupportContactsPage />
          </SupportGuard>
        ),
      },
      { path: '/support-contracts', element: <SupportContractsPage /> },
      { path: '/support-monitor', element: <SupportMonitorPage /> },
      {
        path: '/odoo-helpdesk',
        element: (
          <SupportGuard>
            <SupportTicketsPage key="odoo-helpdesk" odoo />
          </SupportGuard>
        ),
      },
      {
        path: '/telephony-support',
        element: (
          <SupportGuard>
            <TelephonySupportPage />
          </SupportGuard>
        ),
      },
      {
        path: '/communication',
        element: (
          <CommunicationGuard>
            <CommunicationPage />
          </CommunicationGuard>
        ),
      },
      {
        path: '/hr',
        element: (
          <HRGuard>
            <HRPage />
          </HRGuard>
        ),
      },
      { path: '/crm', element: <CrmPage /> },
      { path: '/agents', element: <UserAgentsListPage /> },
      { path: '/account/security', element: <SecurityPage /> },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);

export function Router(): JSX.Element {
  return <RouterProvider router={router} />;
}
